// src/App.tsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './services/AuthContext';
import AuthenticatedLayout from './components/Layout';
import LoginRegister from './components/auth/LoginRegister';
import ResetPassword from './components/auth/reset/ResetPassword';
import Subscription from './components/auth/suscripcion/Subscription';
import ProtectedRoute from './services/ProtectedRoute';
import Verification from './components/auth/verificacion/Verification';
import CreateOrganization from './components/auth/org/CreateOrganization';
import Loader from './components/utils/loader/Loader';
import SuperAdminLayout from "./components/superadmin/SuperAdminLayout"

const App: React.FC = () => (
  <AuthProvider>
    <Router>
      <MainRoutes />
    </Router>
  </AuthProvider>
);

const MainRoutes: React.FC = () => {
  const { isAuthenticated, hasOrganization, userData, logout, syncAuthStatus, hasSubscription, subscriptionStatus } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated !== null && hasSubscription !== null) {
      setIsLoading(false);
    }
  }, [isAuthenticated, hasSubscription]);

  if (isLoading || isAuthenticated === null || hasSubscription === null) return <Loader />;

   // Si el usuario es superadmin, redirigir al panel de superadmin
   if (isAuthenticated && userData?.role === "superadmin") {
    return (
      <Routes>
        <Route path="/superadmin/*" element={<SuperAdminLayout />} />
        <Route path="*" element={<Navigate to="/superadmin" replace />} />
      </Routes>
    )
  }
  
  // Redirigir a /suscripcion si no tiene una suscripción activa
  if (isAuthenticated && hasOrganization && (!hasSubscription || subscriptionStatus === 'inactive')) {
    return (
      <Routes>
        <Route path="/suscripcion" element={<Subscription />} />
        <Route path="*" element={<Navigate to="/suscripcion" replace />} />
      </Routes>
    );
  }
  
  if (isAuthenticated && !userData?.isVerified) {
    return <Navigate to="/verificacion" replace />;
  } 

  if (isAuthenticated && hasOrganization === false) {
    return (
      <Routes>
        <Route path="/crear-organizacion" element={
          <CreateOrganization 
            userData={userData} 
            onOrganizationCreated={syncAuthStatus} 
            handleLogout={logout} 
            syncAuthStatus={syncAuthStatus} 
          />
        } />
        <Route path="*" element={<Navigate to="/crear-organizacion" replace />} />
      </Routes>
    );
  }  

  const defaultPath = userData?.role === 'vendedor' ? '/caja-venta' : '/';

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to={defaultPath} replace /> : <LoginRegister />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/verificacion" element={<Verification />} />
      <Route path="/suscripcion" element={<Subscription />} />
      <Route path="/crear-organizacion" element={
        <ProtectedRoute isAuthenticated={isAuthenticated && !hasOrganization} redirectPath="/login">
          <CreateOrganization userData={userData} onOrganizationCreated={() => {}} handleLogout={logout} syncAuthStatus={syncAuthStatus} />
        </ProtectedRoute>
      } />
      <Route path="/*" element={
        <ProtectedRoute isAuthenticated={isAuthenticated && hasOrganization} role={userData?.role} redirectPath="/login">
          <AuthenticatedLayout />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

export default App;
